import { ChatCitationTarget } from "@/components/common/openChatCitation";

import {
  EvidenceSource,
  chatSourceCalculation,
  chatSourceIodl,
  chatSourceKind,
  chatSourceLabel,
} from "../evidenceFooter";

// What a listed source is called and what it opens. Shared by the sources
// list (#11426) and the calculation card's input rows (#13618), so a row in
// either opens exactly what that source's chip in the prose opens.

/** The sub-line naming what kind of thing a listed source is. */
export const sourceKindLabel = (source: EvidenceSource): string => {
  const sourceKind = chatSourceKind(source.entry);
  switch (sourceKind) {
    case "document":
      return "Document";
    case "record": {
      const kind = source.entry.kind?.replace(/_/g, " ") ?? "";
      return kind ? kind.charAt(0).toUpperCase() + kind.slice(1) : "Record";
    }
    case "interaction":
      return "Interaction";
    case "data":
      return "Data source";
    case "artifact":
      return "Artifact";
    case "calculation":
      return "Calculation";
    default: {
      const unhandled: never = sourceKind;
      throw new Error(`unhandled source kind: ${String(unhandled)}`);
    }
  }
};

/** What a listed source opens — the same target as its citation chip. */
export const sourceTarget = (
  source: EvidenceSource,
  citationQueryId?: string
): ChatCitationTarget => {
  const { entry } = source;
  const displayLabel = chatSourceLabel(entry);
  const sourceKind = chatSourceKind(entry);
  switch (sourceKind) {
    case "data":
      return {
        iodlSource: chatSourceIodl(entry),
        iodlQid: citationQueryId,
        displayLabel,
      };
    case "record":
      return {
        publicId: entry.publicId,
        kind: entry.kind,
        factName: entry.factName,
        displayLabel,
      };
    case "interaction":
      return { interactionId: entry.interactionId, displayLabel };
    case "artifact":
      return { artifactId: entry.artifactId, displayLabel };
    case "calculation":
      return {
        calculation: source.calculation ?? chatSourceCalculation(entry),
        displayLabel,
      };
    case "document":
      return {
        docId: entry.docId,
        pageNum: source.pages[0],
        sourceContent: entry.content || undefined,
        displayLabel,
      };
    default: {
      const unhandled: never = sourceKind;
      throw new Error(`unhandled source kind: ${String(unhandled)}`);
    }
  }
};
